import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { Link } from "react-router-dom";
import { technicianAPI, ticketAPI, categoryAPI } from "../services/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  Clock,
  CheckCircle,
  Filter,
  Search,
  TrendingUp,
  Users,
  ChevronRight,
  Tag,
  Calendar,
  BarChart3,
  Award,
  Target,
  Loader2,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import MultiSelect, { MultiSelectOption } from "../components/MultiSelect";

export default function TechnicianDashboard() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<any[]>([]);
  const [categories, setCategories] = useState<MultiSelectOption[]>([]);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState([
    {
      label: "Assigned to Me",
      value: "0",
      change: "+0 today",
      icon: Users,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      label: "In Progress",
      value: "0",
      change: "Active",
      icon: Clock,
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
    },
    {
      label: "Resolved Today",
      value: "0",
      change: "+0 from yesterday",
      icon: CheckCircle,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      label: "Avg. Resolution",
      value: "0h",
      change: "0% faster",
      icon: TrendingUp,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
  ]);

  useEffect(() => {
    loadTickets();
    loadAllTickets();
    loadFilterOptions();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadTickets();
        loadAllTickets();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await technicianAPI.tickets();

      let ticketsList = [];

      if (response.data?.data && Array.isArray(response.data.data)) {
        ticketsList = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        ticketsList = response.data;
      } else if (Array.isArray(response)) {
        ticketsList = response;
      }

      const formattedTickets = ticketsList.map((ticket: any) => ({
        id: ticket.id,
        ticketNumber: ticket.ticketNumber || "N/A",
        title: ticket.title,
        category: ticket.category?.name || "General",
        priority: ticket.priority?.toLowerCase().replace(/_/g, "-") || "medium",
        status: ticket.status?.toLowerCase().replace(/_/g, "-") || "open",
        time: formatTime(ticket.createdAt),
        user: ticket.user?.fullName || ticket.createdBy?.fullName || "Unknown",
        location: ticket.location || "N/A",
        createdAt: ticket.createdAt,
        resolvedAt: ticket.resolvedAt,
        updatedAt: ticket.updatedAt,
      }));

      setTickets(formattedTickets);

      calculateStats(formattedTickets);

      toast.success("Dashboard Loaded", {
        description: "Assigned tickets retrieved successfully",
      });
    } catch (error) {
      toast.error("Failed to load assigned tickets");
    } finally {
      setLoading(false);
    }
  };

  const loadAllTickets = async () => {
    try {
      const response = await ticketAPI.list({ assignedId: user?.id });

      let ticketsList = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        ticketsList = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        ticketsList = response.data;
      }

      calculatePerformanceMetrics(ticketsList);
    } catch (error) {}
  };

  const calculateStats = (formattedTickets: any[]) => {
    const assignedCount = formattedTickets.length;
    const inProgressCount = formattedTickets.filter(
      (t: any) => t.status === "in-progress",
    ).length;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const resolvedToday = formattedTickets.filter((t: any) => {
      const isResolved = t.status === "resolved" || t.status === "closed";
      if (!isResolved) return false;

      const dateToCheck = t.resolvedAt || t.updatedAt;
      if (!dateToCheck) return false;

      const resolvedDate = new Date(dateToCheck);
      resolvedDate.setHours(0, 0, 0, 0);
      return resolvedDate.getTime() === today.getTime();
    }).length;

    const resolvedTickets = formattedTickets.filter((t: any) => {
      const isResolved = t.status === "resolved" || t.status === "closed";
      const hasResolveDate = t.resolvedAt || t.updatedAt;
      return isResolved && hasResolveDate && t.createdAt;
    });
    let avgResolutionTime = "N/A";
    if (resolvedTickets.length > 0) {
      const totalTime = resolvedTickets.reduce((sum: number, ticket: any) => {
        const created = new Date(ticket.createdAt).getTime();
        const resolveDate = ticket.resolvedAt || ticket.updatedAt;
        const resolved = new Date(resolveDate).getTime();
        return sum + (resolved - created);
      }, 0);
      const avgMs = totalTime / resolvedTickets.length;
      const avgHours = Math.round(avgMs / (1000 * 60 * 60));
      avgResolutionTime = `${avgHours}h`;
    }

    setStats([
      {
        label: "Assigned to Me",
        value: String(assignedCount),
        change: "+0 today",
        icon: Users,
        color: "text-blue-500",
        bgColor: "bg-blue-500/10",
      },
      {
        label: "In Progress",
        value: String(inProgressCount),
        change: "Active",
        icon: Clock,
        color: "text-yellow-500",
        bgColor: "bg-yellow-500/10",
      },
      {
        label: "Resolved Today",
        value: String(resolvedToday),
        change: "+0 from yesterday",
        icon: CheckCircle,
        color: "text-green-500",
        bgColor: "bg-green-500/10",
      },
      {
        label: "Avg. Resolution",
        value: avgResolutionTime,
        change: "0% faster",
        icon: TrendingUp,
        color: "text-purple-500",
        bgColor: "bg-purple-500/10",
      },
    ]);
  };

  const calculatePerformanceMetrics = (ticketsList: any[]) => {
    if (ticketsList.length === 0) {
      setPerformanceData({
        weeklyResolved: 0,
        resolutionRate: 0,
        avgResponseTime: "N/A",
        avgResolutionTime: "N/A",
        userRating: "N/A",
        weeklyActivity: [],
      });
      return;
    }

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const weeklyResolved = ticketsList.filter((t: any) => {
      const isResolved = t.status === "RESOLVED" || t.status === "CLOSED";
      if (!isResolved) return false;

      const dateToCheck = t.resolvedAt || t.updatedAt;
      if (!dateToCheck) return false;

      const resolvedDate = new Date(dateToCheck);
      return resolvedDate >= sevenDaysAgo;
    }).length;

    const totalTickets = ticketsList.length;
    const resolvedTickets = ticketsList.filter(
      (t: any) => t.status === "RESOLVED" || t.status === "CLOSED",
    ).length;
    const resolutionRate =
      totalTickets > 0 ? Math.round((resolvedTickets / totalTickets) * 100) : 0;

    const ticketsWithComments = ticketsList.filter(
      (t: any) => t.comments?.length > 0,
    );
    let avgResponseTime = "N/A";
    if (ticketsWithComments.length > 0) {
      const totalResponseTime = ticketsWithComments.reduce(
        (sum: number, ticket: any) => {
          const created = new Date(ticket.createdAt).getTime();
          const firstComment = ticket.comments[0];
          const responded = new Date(firstComment.createdAt).getTime();
          return sum + (responded - created);
        },
        0,
      );
      const avgMs = totalResponseTime / ticketsWithComments.length;
      const avgMinutes = Math.round(avgMs / (1000 * 60));
      avgResponseTime = `${avgMinutes}min`;
    } else {
    }

    const resolvedTicketsForAvg = ticketsList.filter((t: any) => {
      const isResolved = t.status === "RESOLVED" || t.status === "CLOSED";
      const hasResolveDate = t.resolvedAt || t.updatedAt;
      return isResolved && hasResolveDate && t.createdAt;
    });
    let avgResolutionTime = "N/A";
    if (resolvedTicketsForAvg.length > 0) {
      const totalTime = resolvedTicketsForAvg.reduce(
        (sum: number, ticket: any) => {
          const created = new Date(ticket.createdAt).getTime();
          const resolveDate = ticket.resolvedAt || ticket.updatedAt;
          const resolved = new Date(resolveDate).getTime();
          return sum + (resolved - created);
        },
        0,
      );
      const avgMs = totalTime / resolvedTicketsForAvg.length;
      const avgHours = Math.round(avgMs / (1000 * 60 * 60));
      avgResolutionTime = `${avgHours}h`;
    }

    const ticketsWithFeedback = ticketsList.filter(
      (t: any) => t.feedbacks?.length > 0,
    );
    let userRating = "N/A";
    if (ticketsWithFeedback.length > 0) {
      const totalRating = ticketsWithFeedback.reduce(
        (sum: number, ticket: any) => sum + ticket.feedbacks[0].rating,
        0,
      );
      const avgRating = (totalRating / ticketsWithFeedback.length).toFixed(1);
      userRating = `${avgRating}/5`;
    } else {
    }

    const weeklyActivity = [];
    const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    const today = new Date();

    for (let i = 4; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);

      const dayResolved = ticketsList.filter((t: any) => {
        const isResolved = t.status === "RESOLVED" || t.status === "CLOSED";
        if (!isResolved) return false;

        const dateToCheck = t.resolvedAt || t.updatedAt;
        if (!dateToCheck) return false;

        const resolvedDate = new Date(dateToCheck);
        return resolvedDate >= date && resolvedDate < nextDate;
      }).length;

      const dayNew = ticketsList.filter((t: any) => {
        const createdDate = new Date(t.createdAt);
        return createdDate >= date && createdDate < nextDate;
      }).length;

      weeklyActivity.push({
        day:
          dayNames[date.getDay() === 0 ? 6 : date.getDay() - 1] ||
          date.toLocaleDateString("en-US", { weekday: "short" }),
        resolved: dayResolved,
        new: dayNew,
      });
    }

    setPerformanceData({
      weeklyResolved,
      resolutionRate,
      avgResponseTime,
      avgResolutionTime,
      userRating,
      weeklyActivity,
    });
  };

  const formatTime = (dateString: string): string => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const loadFilterOptions = async () => {
    try {
      const categoriesResponse = await categoryAPI.list();
      if (categoriesResponse.success && categoriesResponse.data) {
        const categoryList = Array.isArray(categoriesResponse.data)
          ? categoriesResponse.data
          : categoriesResponse.data.items || [];

        const categoryOptions = categoryList.map((cat: any) => ({
          value: cat.name,
          label: cat.name,
          color: "bg-blue-500/10 text-blue-400",
        }));

        setCategories(categoryOptions);
      }
    } catch (error) {
      console.error("Failed to load filter options:", error);
      toast.error("Failed to load filter options");
    }
  };

  const statusOptions: MultiSelectOption[] = [
    {
      value: "open",
      label: "Open",
      color: "bg-red-500/10 text-red-400",
      badge: true,
    },
    {
      value: "in-progress",
      label: "In Progress",
      color: "bg-blue-500/10 text-blue-400",
      badge: true,
    },
    {
      value: "pending",
      label: "Pending",
      color: "bg-yellow-500/10 text-yellow-400",
      badge: true,
    },
    {
      value: "resolved",
      label: "Resolved",
      color: "bg-green-500/10 text-green-400",
      badge: true,
    },
    {
      value: "closed",
      label: "Closed",
      color: "bg-gray-500/10 text-gray-400",
      badge: true,
    },
  ];

  const priorityOptions: MultiSelectOption[] = [
    {
      value: "critical",
      label: "Critical",
      color: "bg-red-500/10 text-red-400",
      badge: true,
    },
    {
      value: "high",
      label: "High",
      color: "bg-orange-500/10 text-orange-400",
      badge: true,
    },
    {
      value: "medium",
      label: "Medium",
      color: "bg-yellow-500/10 text-yellow-400",
      badge: true,
    },
    {
      value: "low",
      label: "Low",
      color: "bg-blue-500/10 text-blue-400",
      badge: true,
    },
  ];

  const filteredTickets = tickets.filter((ticket: any) => {
    const status = (ticket.status || "").toLowerCase().replace(/_/g, "-");
    const priority = (ticket.priority || "").toLowerCase();
    const category = ticket.category || "";

    const matchesStatus =
      selectedStatuses.length === 0 || selectedStatuses.includes(status);

    const matchesPriority =
      selectedPriorities.length === 0 || selectedPriorities.includes(priority);

    const matchesCategory =
      selectedCategories.length === 0 || selectedCategories.includes(category);

    const matchesSearch =
      searchQuery === "" ||
      (ticket.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ticket.ticketNumber || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (ticket.user || "").toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesPriority && matchesCategory && matchesSearch;
  });

  const hasActiveFilters =
    selectedStatuses.length > 0 ||
    selectedPriorities.length > 0 ||
    selectedCategories.length > 0 ||
    searchQuery.trim() !== "";

  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedPriorities([]);
    setSelectedCategories([]);
    setSearchQuery("");
  };

  const statusColors = {
    open: "bg-red-500/10 text-red-400",
    "in-progress": "bg-blue-500/10 text-blue-400",
    pending: "bg-yellow-500/10 text-yellow-400",
  };

  const priorityColors = {
    critical: "text-red-500",
    high: "text-orange-500",
    medium: "text-yellow-500",
    low: "text-blue-500",
  };

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 font-serif">
            Technician Console
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Manage assigned tickets and monitor queue status
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 hover:border-primary/30 transition-all shadow-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <div
                    className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-foreground mb-1 font-mono">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-primary">{stat.change}</div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden"
        >
          <div className="border-b border-border/50 p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="text-xl font-bold text-foreground font-serif">
                Ticket Queue
              </h2>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="search"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm
                  transition-all whitespace-nowrap
                  ${
                    showFilters || hasActiveFilters
                      ? "bg-primary text-white"
                      : "bg-muted/50 text-foreground hover:bg-muted"
                  }
                `}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && (
                  <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                    {[
                      selectedStatuses.length,
                      selectedPriorities.length,
                      selectedCategories.length,
                    ].reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm bg-destructive/10 text-destructive hover:bg-destructive/20 transition-all whitespace-nowrap"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-border/50"
              >
                <MultiSelect
                  label="Status"
                  options={statusOptions}
                  value={selectedStatuses}
                  onChange={setSelectedStatuses}
                  placeholder="Select statuses..."
                  maxDisplay={2}
                />

                <MultiSelect
                  label="Priority"
                  options={priorityOptions}
                  value={selectedPriorities}
                  onChange={setSelectedPriorities}
                  placeholder="Select priorities..."
                  maxDisplay={2}
                />

                <MultiSelect
                  label="Category"
                  options={categories}
                  value={selectedCategories}
                  onChange={setSelectedCategories}
                  placeholder="Select categories..."
                  maxDisplay={2}
                />
              </motion.div>
            )}

            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/50">
                <span className="text-xs text-muted-foreground font-medium">
                  Active Filters:
                </span>
                {selectedStatuses.map((status) => (
                  <span
                    key={status}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium"
                  >
                    Status: {status}
                    <button
                      onClick={() =>
                        setSelectedStatuses(
                          selectedStatuses.filter((s) => s !== status),
                        )
                      }
                      className="hover:bg-black/10 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedPriorities.map((priority) => (
                  <span
                    key={priority}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500/10 text-orange-400 rounded text-xs font-medium"
                  >
                    Priority: {priority}
                    <button
                      onClick={() =>
                        setSelectedPriorities(
                          selectedPriorities.filter((p) => p !== priority),
                        )
                      }
                      className="hover:bg-black/10 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedCategories.map((category) => (
                  <span
                    key={category}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs font-medium"
                  >
                    Category: {category}
                    <button
                      onClick={() =>
                        setSelectedCategories(
                          selectedCategories.filter((c) => c !== category),
                        )
                      }
                      className="hover:bg-black/10 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="divide-y divide-border/50">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center p-12">
                <p className="text-muted-foreground">No tickets found</p>
              </div>
            ) : (
              filteredTickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/dashboard/ticket/${ticket.id}`}
                  className="block p-4 sm:p-6 hover:bg-muted/30 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`mt-1 w-1 h-16 rounded-full flex-shrink-0 ${
                        ticket.priority === "critical"
                          ? "bg-red-500"
                          : ticket.priority === "high"
                            ? "bg-orange-500"
                            : ticket.priority === "medium"
                              ? "bg-yellow-500"
                              : "bg-blue-500"
                      }`}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-sm font-mono font-semibold text-primary">
                          {ticket.ticketNumber}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            statusColors[
                              ticket.status as keyof typeof statusColors
                            ]
                          }`}
                        >
                          {ticket.status.replace("-", " ")}
                        </span>
                        <span
                          className={`text-xs font-semibold ${
                            priorityColors[
                              ticket.priority as keyof typeof priorityColors
                            ]
                          }`}
                        >
                          {ticket.priority.toUpperCase()}
                        </span>
                      </div>

                      <h3 className="text-sm sm:text-base font-semibold text-foreground mb-2">
                        {ticket.title}
                      </h3>

                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          <span>{ticket.category}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>{ticket.user}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{ticket.time}</span>
                        </div>
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </Link>
              ))
            )}
          </div>

          <div className="border-t border-border/50 p-4 bg-muted/20 text-center">
            <p className="text-sm text-muted-foreground">
              Showing {filteredTickets.length} of {tickets.length} tickets
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-border/50 rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-4 sm:p-6 border-b border-border/50 bg-muted/20">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-foreground font-serif flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  My Performance Summary
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Track your performance metrics and efficiency
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-blue-400" />
                  <p className="text-xs font-medium text-blue-400">This Week</p>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {performanceData?.weeklyResolved || 0}
                </p>
                <p className="text-xs text-muted-foreground">
                  Tickets Resolved
                </p>
              </div>

              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4 text-green-400" />
                  <p className="text-xs font-medium text-green-400">
                    Resolution Rate
                  </p>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {performanceData?.resolutionRate || 0}%
                </p>
                <p className="text-xs text-muted-foreground">Success Rate</p>
              </div>

              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-purple-400" />
                  <p className="text-xs font-medium text-purple-400">
                    Avg. Response
                  </p>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {performanceData?.avgResponseTime || "N/A"}
                </p>
                <p className="text-xs text-muted-foreground">
                  First Response Time
                </p>
              </div>

              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-yellow-400" />
                  <p className="text-xs font-medium text-yellow-400">
                    User Rating
                  </p>
                </div>
                <p className="text-2xl font-bold text-foreground">
                  {performanceData?.userRating || "N/A"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Customer Satisfaction
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Weekly Activity
              </h3>
              {(performanceData?.weeklyActivity || []).map((item: any) => (
                <div key={item.day} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{item.day}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-green-400">
                        {item.resolved} resolved
                      </span>
                      <span className="text-blue-400">{item.new} new</span>
                    </div>
                  </div>
                  <div className="flex gap-1 h-2">
                    <div
                      className="bg-green-500 rounded-full"
                      style={{ width: `${(item.resolved / 10) * 100}%` }}
                    />
                    <div
                      className="bg-blue-500 rounded-full"
                      style={{ width: `${(item.new / 10) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border/30">
              <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Award className="w-4 h-4 text-primary" />
                Performance Summary
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Resolved {performanceData?.weeklyResolved || 0} tickets this
                    week
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Maintaining {performanceData?.resolutionRate || 0}%
                    resolution rate
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Average response time:{" "}
                    {performanceData?.avgResponseTime || "N/A"}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <span>
                    Average resolution time:{" "}
                    {performanceData?.avgResolutionTime || "N/A"}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
